import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Building2, 
  Users, 
  Shield, 
  Settings, 
  BarChart3,
  MapPin,
  UserCheck,
  Layers,
  Award
} from 'lucide-react';
import { apiClient } from '../../../services/api/client';
import { useDraggableTabs } from '../../../components/common/hooks/useDraggableTabs';
import clsx from 'clsx';

const OrganisationDashboard: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Navigation items par défaut
  const defaultNavigationItems = [
    { path: 'sites', label: 'Sites', icon: MapPin },
    { path: 'employees', label: 'Employés', icon: Users },
    { path: 'departments', label: 'Départements', icon: Building2 },
    { path: 'roles', label: 'Rôles QHSE', icon: Shield },
    { path: 'positions', label: 'Postes', icon: UserCheck },
    { path: 'competences', label: 'Compétences', icon: Award },
    { path: 'hierarchy', label: 'Hiérarchie', icon: Layers },
    { path: 'organigramme', label: 'Organigramme', icon: Layers },
    { path: 'cache', label: 'Cache', icon: Settings },
  ];

  // Utiliser le hook de drag & drop
  const {
    tabs: navigationItems,
    draggedIndex,
    dragOverIndex,
    handleDragStart,
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
    resetTabs,
    getFirstTabPath,
  } = useDraggableTabs({
    defaultTabs: defaultNavigationItems,
    storageKey: 'organisation-tab-order',
  });

  const [stats, setStats] = useState([
    { name: 'Sites actifs', value: '0', color: 'bg-blue-500', path: 'sites', loading: true },
    { name: 'Employés', value: '0', color: 'bg-green-500', path: 'employees', loading: true },
    { name: 'Départements', value: '0', color: 'bg-purple-500', path: 'departments', loading: true },
    { name: 'Rôles QHSE', value: '0', color: 'bg-orange-500', path: 'roles', loading: true },
  ]);

  useEffect(() => {
    const loadStats = async () => {
      try {
        const updateStat = (index: number, value: string) => {
          setStats(prev => prev.map((stat, i) => 
            i === index ? { ...stat, value, loading: false } : stat
          ));
        };

        const [sitesRes, employeesRes, departmentsRes, rolesRes] = await Promise.allSettled([
          apiClient.get('/organisation/sites'),
          apiClient.get('/organisation/employees'),
          apiClient.get('/organisation/departments'),
          apiClient.get('/organisation/roles'),
        ]);

        if (sitesRes.status === 'fulfilled') {
          updateStat(0, sitesRes.value.length.toString());
        }
        if (employeesRes.status === 'fulfilled') {
          updateStat(1, employeesRes.value.length.toString());
        }
        if (departmentsRes.status === 'fulfilled') {
          updateStat(2, departmentsRes.value.length.toString());
        }
        if (rolesRes.status === 'fulfilled') {
          const qhseRoles = rolesRes.value.filter((r: { isQhseRole: boolean }) => r.isQhseRole);
          updateStat(3, qhseRoles.length.toString());
        }
      } catch (error) {
        console.error('Erreur lors du chargement des statistiques:', error);
      }
    };

    loadStats();
  }, []);

  // Rediriger vers le premier onglet si on est sur /organisation
  useEffect(() => {
    if (location.pathname === '/organisation' || location.pathname === '/organisation/') {
      const firstPath = getFirstTabPath();
      if (firstPath) {
        navigate(`/organisation/${firstPath}`, { replace: true });
      }
    }
  }, [location.pathname, navigate, getFirstTabPath]);

  const handleStatClick = (path: string) => {
    navigate(`/organisation/${path}`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Organisation</h1>
            <p className="text-gray-600 mt-1">
              Gestion des sites, employés, départements et rôles QHSE
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={resetTabs}
              className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
            >
              <Settings className="h-4 w-4" />
              <span>Réinitialiser l'ordre</span>
            </button>
          </div>
        </div>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <div
            key={stat.path}
            onClick={() => handleStatClick(stat.path)}
            className="bg-white rounded-lg shadow border border-gray-200 p-6 cursor-pointer hover:shadow-md transition-shadow"
          >
            <div className="flex items-center">
              <div className={`flex-shrink-0 ${stat.color} rounded-md p-3`}>
                <Shield className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                {stat.loading ? (
                  <div className="animate-pulse">
                    <div className="h-8 bg-gray-200 rounded w-12 mb-1"></div>
                    <div className="h-4 bg-gray-200 rounded w-20"></div>
                  </div>
                ) : (
                  <>
                    <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
                    <div className="text-sm text-gray-600">{stat.name}</div>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Navigation draggable */}
      <div className="bg-white rounded-lg shadow border border-gray-200">
        <div className="border-b border-gray-200 px-6 py-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              💡 Glissez-déposez les onglets pour les réorganiser
            </p>
          </div>
        </div>
        <nav className="flex flex-wrap border-b border-gray-200 px-4">
          {navigationItems.map((item, index) => {
            const isActive = location.pathname.includes(`/organisation/${item.path}`);
            const isDragging = draggedIndex === index;
            const isDragOver = dragOverIndex === index;
            const Icon = item.icon;

            return (
              <div
                key={item.path}
                className={clsx(
                  'relative flex items-center transition-all duration-200',
                  isDragging && 'opacity-50',
                  isDragOver && 'border-t-2 border-blue-500',
                  'hover:bg-gray-50'
                )}
              >
                {/* Zone de drag (poignée) */}
                <div
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragEnter={() => handleDragEnter(index)}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={() => handleDrop(index)}
                  onDragEnd={handleDragEnd}
                  className="cursor-move p-1 hover:bg-gray-200 rounded"
                  title="Glisser pour réorganiser"
                >
                  <svg className="h-4 w-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2zM14 9a1 1 0 00-1 1v2a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 00-1-1h-2z" />
                  </svg>
                </div>

                {/* Zone de clic (onglet) */}
                <div
                  onClick={() => navigate(`/organisation/${item.path}`)}
                  className={clsx(
                    'flex items-center space-x-2 px-4 py-3 text-sm font-medium transition-colors cursor-pointer',
                    isActive
                      ? 'text-blue-600 border-b-2 border-blue-600'
                      : 'text-gray-600 hover:text-gray-900'
                  )}
                >
                  {Icon && <Icon className="h-4 w-4" />}
                  <span>{item.label}</span>
                </div>
              </div>
            );
          })}
        </nav>
      </div>

      {/* Message d'accueil */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Bienvenue dans l'Organisation
          </h2>
          <p className="text-gray-600 mb-4">
            Sélectionnez une section dans les onglets ci-dessus pour commencer à gérer votre organisation.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            {navigationItems.slice(0, 4).map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.path}
                  onClick={() => navigate(`/organisation/${item.path}`)}
                  className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                >
                    {Icon && <Icon className="h-8 w-8 mx-auto mb-2 text-gray-600" />}
                    <div className="text-sm font-medium text-gray-900">{item.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
  );
};

export default OrganisationDashboard;